import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { currentTenantId } from '@/utils/admin-utils';
import { ProfiNeoSyncStateItemDto, ProfiNeoSyncStateService } from '@artemis-software/wr-api';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { NbComponentStatus, NbToastrService } from '@nebular/theme';
import { firstValueFrom, map } from 'rxjs';

const MAX_BUILDING_SECTIONS = 25;

@Component({
  standalone: false,
  selector: 'wr-building-profi-neo-sanierung-sync-page',
  templateUrl: './building-profi-neo-sanierung-sync-page.component.html',
  styleUrls: ['./building-profi-neo-sanierung-sync-page.component.scss']
})
export class BuildingProfiNeoSanierungSyncPageComponent {
  currentTenantId = currentTenantId();
  readonly profiNeoSyncStateService = inject(ProfiNeoSyncStateService);
  readonly nbToastrService = inject(NbToastrService);

  readonly buildingId = input.required<string | null>();
  readonly internalNumber = input.required<string | null>();
  readonly sectionCount = input.required<number | null>();
  private readonly refreshTrigger = signal(0);
  readonly syncInProgress = signal(false);

  readonly statusColorMap: Record<ProfiNeoSyncStateItemDto.SyncStatusEnum, NbComponentStatus> = {
    PENDING: 'warning',
    QUEUED: 'info',
    COMPLETED: 'success',
    FAILED: 'danger',
  } as const;

  profiNeoSyncState = asyncComputed(() => {
    const buildingId = this.buildingId();
    this.refreshTrigger();
    if (!buildingId) return undefined;
    return this.profiNeoSyncStateService.findAll({
      buildingId: buildingId,
      internalTenantId: this.currentTenantId(),
      syncType: ProfiNeoSyncStateItemDto.SyncTypeEnum.Sanierung,
      size: 1,
      sort: {
        sort: 'created',
        order: 'DESC'
      }
    }).pipe(
      map(syncStateList => syncStateList?.[0])
    );
  });

  readonly sectionAddressIds = computed(() => {
    const csv = this.profiNeoSyncState()?.profiNeoSectionAddressIds;
    if (!csv) return [];
    return csv.split(',').map(id => id.trim()).filter(id => id.length > 0);
  });

  readonly blockingReason = computed(() => {
    const internalNumber = this.internalNumber();
    const sectionCount = this.sectionCount();
    if (internalNumber === null || sectionCount === null) {
      return null;
    }
    const trimmedNumber = internalNumber.trim();
    if (trimmedNumber.length !== 5) {
      return `Synchronisierung nicht möglich: Die Objektnummer muss genau 5 Zeichen haben – aktuell: „${trimmedNumber}“ (${trimmedNumber.length} Zeichen).`;
    }
    if (sectionCount === 0) {
      return 'Synchronisierung nicht möglich: Das Gebäude hat keine Gebäudeteile – es wird mindestens ein Gebäudeteil benötigt.';
    }
    if (sectionCount > MAX_BUILDING_SECTIONS) {
      return `Synchronisierung nicht möglich: Das Gebäude hat ${sectionCount} Gebäudeteile – erlaubt sind maximal ${MAX_BUILDING_SECTIONS}.`;
    }
    return null;
  });

  readonly isBlocked = computed(() => this.blockingReason() !== null);

  readonly isSynced = computed(() => {
    const syncStatus = this.profiNeoSyncState()?.syncStatus;
    return syncStatus === 'COMPLETED';
  });

  readonly isProcessing = computed(() => {
    const syncStatus = this.profiNeoSyncState()?.syncStatus;
    return syncStatus === 'PENDING' || syncStatus === 'QUEUED';
  });

  readonly canShowSyncButton = computed(() => {
    const syncStatus = this.profiNeoSyncState()?.syncStatus;
    return !syncStatus || syncStatus === 'FAILED';
  });

  constructor() {
    effect(onCleanup => {
      if (!this.isProcessing()) return;
      const intervalId = setInterval(() => {
        this.refreshTrigger.update(previousRefreshCount => previousRefreshCount + 1);
      }, 4000);
      onCleanup(() => clearInterval(intervalId));
    });
  }

  async synchronizeBuilding() {
    const buildingId = this.buildingId();
    if (!buildingId) {
      this.nbToastrService.danger('Gebäude konnte nicht gefunden werden', 'Gebäude');
      return;
    }
    if (this.isBlocked()) {
      return;
    }
    try {
      this.syncInProgress.set(true);
      await firstValueFrom(this.profiNeoSyncStateService.merge({
        buildingId,
        syncType: ProfiNeoSyncStateItemDto.SyncTypeEnum.Sanierung,
      }));
      this.nbToastrService.success('Synchronisierung gestartet', 'Gebäude');
      this.refreshTrigger.update(previousRefreshCount => previousRefreshCount + 1);
    } catch (error) {
      this.nbToastrService.danger('Fehler bei der Synchronisierung', 'Gebäude');
    } finally {
      this.syncInProgress.set(false);
    }
  }
}
